<script setup lang="ts">
import { Loader } from '@googlemaps/js-api-loader'

interface Props {
  apiKey: string;
  mapId?: string;
  config?: object;
  center?: google.maps.LatLng;
  zoom?: number;
  bounds?: Array<MaybeRefOrGetter<google.maps.LatLngLiteral | null>>;
}

const { apiKey, config = ({}), center = ({ lat: 51.90, lng: 19.59 } as unknown as google.maps.LatLng), zoom = 8, mapId, ...props } = defineProps<Props>()

const { bounds = [
  { lat: 50.00, lng: 14.00 },
  { lat: 50.00, lng: 24.00 },
  { lat: 54.00, lng: 14.00 },
  { lat: 54.00, lng: 24.00 }
] } = reactive(props)

const loader = new Loader({
  apiKey
})

const mapElement = useTemplateRef('mapElement')
const map = shallowRef<google.maps.Map | null>(null)
const google = shallowRef<typeof globalThis.google | null>(null)

onMounted(async () => {
  const [{ Map }, { LatLngBounds }] = await Promise.all([
    loader.importLibrary('maps'),
    loader.importLibrary('core')
  ])

  google.value = window.google

  map.value = new Map(mapElement.value as HTMLElement, {
    ...config,
    mapId,
    zoom,
    center,
    mapTypeControl: false,
    streetViewControl: false,
    gestureHandling: 'cooperative'
  })

  watch(bounds, () => {
    const mapBounds = new LatLngBounds()
    bounds.filter(point => Boolean(toValue(point))).forEach(point => mapBounds.extend(toValue(point) as google.maps.LatLngLiteral))
    map.value?.fitBounds(mapBounds, 85)
  }, { immediate: true })
})

provide(GoogleMapsInjection, {
  loader,
  google,
  map
})

defineExpose({
  map
})
</script>

<template>
  <div>
    <div ref="mapElement" class="w-full h-full" />
    <template v-if="!!map && !!google">
      <slot :google="google" />
    </template>
  </div>
</template>